<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  quoteTemplates: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['send', 'sendQuote', 'generateQuotePdf']);

const copy = {
  contractTitle: 'Enviar contrato',
  quoteTitle: 'Enviar orçamento',
  contractDescription:
    'Selecione a caixa e, se desejar, um template para enviar o link.',
  quoteDescription:
    'Selecione a caixa e o modelo para enviar o orçamento ao contato.',
  inbox: 'Caixa de entrada',
  template: 'Template',
  noTemplate: 'Enviar mensagem simples',
  quoteTemplate: 'Modelo de orçamento',
  defaultQuoteTemplate: 'Modelo padrão',
  cancel: 'Cancelar',
  confirm: 'Enviar',
  generatePdf: 'Gerar PDF',
  copySignatureLink: 'Copiar link de assinatura',
  noInbox: 'Nenhuma caixa disponível para envio.',
  customer: 'Cliente',
  document: 'CPF/CNPJ',
  phone: 'Telefone',
  items: 'Itens',
  total: 'Total',
};

const dialogRef = ref(null);
const contract = ref(null);
const quotePayload = ref(null);
const form = reactive({
  inboxId: '',
  templateIndex: '',
  quoteTemplateId: '',
});

const inboxes = useMapGetter('inboxes/getInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const selectedInbox = computed(() =>
  inboxes.value.find(inbox => Number(inbox.id) === Number(form.inboxId))
);

const templates = computed(() =>
  form.inboxId ? getFilteredWhatsAppTemplates.value(form.inboxId) || [] : []
);

const selectedTemplate = computed(() =>
  form.templateIndex ? templates.value[Number(form.templateIndex)] : null
);

const activeQuoteTemplates = computed(() =>
  props.quoteTemplates.filter(template => template.active !== false)
);

const isQuoteMode = computed(() => !!quotePayload.value);
const quoteItems = computed(() => quotePayload.value?.invoice_items || []);
const quoteTotal = computed(() =>
  quoteItems.value.reduce((total, item) => {
    const quantity = Number(item.quantity || 1);
    return total + quantity * Number(item.unit_price || 0);
  }, 0)
);
const formattedQuoteTotal = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(quoteTotal.value)
);
const title = computed(() =>
  isQuoteMode.value ? copy.quoteTitle : copy.contractTitle
);
const description = computed(() =>
  isQuoteMode.value ? copy.quoteDescription : copy.contractDescription
);
const isConfirmDisabled = computed(
  () => !form.inboxId || (!contract.value && !quotePayload.value)
);

const templateLabel = template => template.name || template.friendly_name;

const findBodyComponent = template =>
  Array(template?.components).find(component => component.type === 'BODY');

const firstBodyVariable = template => {
  const body = findBodyComponent(template)?.text || template?.body || '';
  const match = body.match(/\{\{\s*([\w.]+)\s*\}\}/);
  return match?.[1];
};

const buildTemplateParams = template => {
  if (!template || !contract.value) return null;

  const bodyVariable = firstBodyVariable(template);
  const processedParams = bodyVariable
    ? { body: { [bodyVariable]: contract.value.public_url } }
    : {};

  return {
    name: template.name || template.friendly_name,
    namespace: template.namespace,
    language: template.language || 'pt_BR',
    processed_params: processedParams,
  };
};

const open = nextContract => {
  contract.value = nextContract;
  quotePayload.value = null;
  form.inboxId = '';
  form.templateIndex = '';
  form.quoteTemplateId = '';
  dialogRef.value?.open();
};

const openQuote = nextQuotePayload => {
  quotePayload.value = nextQuotePayload;
  contract.value = null;
  form.inboxId = '';
  form.templateIndex = '';
  form.quoteTemplateId = activeQuoteTemplates.value[0]?.id || '';
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleInboxChange = () => {
  form.templateIndex = '';
};

const handleConfirm = () => {
  if (isConfirmDisabled.value) return;

  if (isQuoteMode.value) {
    emit('sendQuote', {
      ...quotePayload.value,
      inbox_id: form.inboxId,
      quote_template_id: form.quoteTemplateId,
    });
    close();
    return;
  }

  emit('send', {
    contract: contract.value,
    payload: {
      inbox_id: form.inboxId,
      template_params: buildTemplateParams(selectedTemplate.value),
    },
  });
  close();
};

const handleGenerateQuotePdf = () => {
  if (!quotePayload.value) return;

  emit('generateQuotePdf', {
    ...quotePayload.value,
    quote_template_id: form.quoteTemplateId,
  });
};

const copySignatureLink = async () => {
  const signatureLink = quotePayload.value?.public_url;
  if (!signatureLink) return;

  try {
    await navigator.clipboard.writeText(signatureLink);
    useAlert('Link de assinatura copiado.');
  } catch {
    useAlert('Não foi possível copiar o link de assinatura.');
  }
};

defineExpose({ open, openQuote, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="title"
    :description="description"
    :cancel-button-label="copy.cancel"
    :confirm-button-label="copy.confirm"
    :disable-confirm-button="isConfirmDisabled"
    @confirm="handleConfirm"
  >
    <div class="grid gap-4">
      <p v-if="!inboxes.length" class="mb-0 text-sm text-n-slate-11">
        {{ copy.noInbox }}
      </p>
      <label v-else class="grid gap-1.5 text-sm font-medium text-n-slate-12">
        {{ copy.inbox }}
        <select
          v-model="form.inboxId"
          class="text-n-slate-12 block h-10 w-full px-3 py-2 pr-6 mb-0 border-0 shadow-sm appearance-none rounded-xl select-caret leading-6 bg-n-background"
          @change="handleInboxChange"
        >
          <option value="">{{ copy.inbox }}</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name || inbox.channel_type }}
          </option>
        </select>
      </label>

      <label
        v-if="isQuoteMode && activeQuoteTemplates.length"
        class="grid gap-1.5 text-sm font-medium text-n-slate-12"
      >
        {{ copy.quoteTemplate }}
        <select
          v-model="form.quoteTemplateId"
          class="text-n-slate-12 block h-10 w-full px-3 py-2 pr-6 mb-0 border-0 shadow-sm appearance-none rounded-xl select-caret leading-6 bg-n-background"
        >
          <option value="">{{ copy.defaultQuoteTemplate }}</option>
          <option
            v-for="template in activeQuoteTemplates"
            :key="template.id"
            :value="template.id"
          >
            {{ template.name }}
          </option>
        </select>
      </label>

      <label
        v-if="!isQuoteMode && selectedInbox && templates.length"
        class="grid gap-1.5 text-sm font-medium text-n-slate-12"
      >
        {{ copy.template }}
        <select
          v-model="form.templateIndex"
          class="text-n-slate-12 block h-10 w-full px-3 py-2 pr-6 mb-0 border-0 shadow-sm appearance-none rounded-xl select-caret leading-6 bg-n-background"
        >
          <option value="">{{ copy.noTemplate }}</option>
          <option
            v-for="(template, index) in templates"
            :key="template.id || template.name || index"
            :value="index"
          >
            {{ templateLabel(template) }}
          </option>
        </select>
      </label>

      <div
        v-if="isQuoteMode"
        class="grid gap-2 rounded-xl border border-n-weak bg-n-alpha-1 p-3 text-sm"
      >
        <div class="flex items-center justify-between gap-3">
          <span class="text-n-slate-10">{{ copy.customer }}</span>
          <strong class="text-right text-n-slate-12">
            {{ quotePayload.recipient_name || 'Não informado' }}
          </strong>
        </div>
        <div class="flex items-center justify-between gap-3">
          <span class="text-n-slate-10">{{ copy.document }}</span>
          <span class="text-right text-n-slate-12">
            {{ quotePayload.recipient_cpf || 'Não informado' }}
          </span>
        </div>
        <div class="flex items-center justify-between gap-3">
          <span class="text-n-slate-10">{{ copy.phone }}</span>
          <span class="text-right text-n-slate-12">
            {{ quotePayload.recipient_phone || 'Não informado' }}
          </span>
        </div>
        <div class="flex items-center justify-between gap-3">
          <span class="text-n-slate-10">{{ copy.items }}</span>
          <span class="text-right text-n-slate-12">
            {{ quoteItems.length }}
          </span>
        </div>
        <div
          class="flex items-center justify-between gap-3 border-t border-n-weak pt-2"
        >
          <strong class="text-n-slate-12">{{ copy.total }}</strong>
          <strong class="text-n-slate-12">{{ formattedQuoteTotal }}</strong>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="grid w-full grid-cols-2 gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="copy.cancel"
          class="w-full"
          type="button"
          @click="close"
        />
        <Button
          v-if="isQuoteMode"
          variant="outline"
          color="slate"
          :label="copy.generatePdf"
          class="w-full"
          type="button"
          @click="handleGenerateQuotePdf"
        />
        <Button
          v-if="isQuoteMode && quotePayload.public_url"
          variant="outline"
          color="slate"
          icon="i-lucide-copy"
          :label="copy.copySignatureLink"
          class="w-full"
          type="button"
          @click="copySignatureLink"
        />
        <Button
          color="blue"
          :label="copy.confirm"
          class="w-full"
          :disabled="isConfirmDisabled"
          type="submit"
        />
      </div>
    </template>
  </Dialog>
</template>
